import { io, Socket } from 'socket.io-client';
import { v4 as uuidv4 } from 'uuid';
import { orderBookKucoinToCex } from './cex-engine.service';
import { AggregatedMarketStatsData, CandlesFromInfluxData, ConnectionStatusCex, KlinesData, MarketStatsData, OrderAction, OrderBookResponse, SocketAggregatedMarketStatsRequest, SocketCancelOrderRequest, SocketCandlesRequest, SocketKlinesRequest, SocketMarketStatsRequest, SocketOrderbookRequest, SocketOrderRequest, SocketTradesRequest, TradeDataCex, WebSocketResponse } from '../types/cex-trade';
import { OrderBookData } from './trading-data.service';

// ================= Socket.IO Service ================= //

export interface CexEngineSocketService {
  socket: Socket;
  connect: () => void;
  disconnect: () => void;
  isConnected: () => boolean;

  // Authentication
  authenticate: (token: string) => Promise<WebSocketResponse>;
  getConnectionStatus: () => Promise<ConnectionStatusCex>;

  // Order Management
  addOrder: (orderData: SocketOrderRequest) => Promise<WebSocketResponse>;
  cancelOrder: (cancelData: SocketCancelOrderRequest) => Promise<WebSocketResponse>;

  // Market Data Subscriptions
  subscribeKlines: (request: SocketKlinesRequest) => Promise<WebSocketResponse>;
  unsubscribeKlines: (symbol: string) => Promise<WebSocketResponse>;
  subscribeOrderbook: (request: SocketOrderbookRequest) => Promise<WebSocketResponse>;
  unsubscribeOrderbook: (symbol: string) => Promise<WebSocketResponse>;
  subscribeMarketStats: (request: SocketMarketStatsRequest) => Promise<WebSocketResponse>;
  unsubscribeMarketStats: (symbol: string) => Promise<WebSocketResponse>;
  subscribeTrades: (request: SocketTradesRequest) => Promise<WebSocketResponse>;
  unsubscribeTrades: (symbol: string) => Promise<WebSocketResponse>;

  // InfluxDB Data
  getCandlesFromInflux: (request: SocketCandlesRequest) => Promise<WebSocketResponse<CandlesFromInfluxData>>;
  getAggregatedMarketStats: (request: SocketAggregatedMarketStatsRequest) => Promise<WebSocketResponse<AggregatedMarketStatsData>>;

  // Redis Cache Data
  getOrderBookFromRedis: (request: SocketOrderbookRequest) => Promise<WebSocketResponse<OrderBookResponse>>;
  getMarketStatsFromRedis: (request: SocketMarketStatsRequest) => Promise<WebSocketResponse<MarketStatsData>>;

  // Utility
  ping: () => void;
  subscribe: (channels: string[]) => void;
  unsubscribe: (channels: string[]) => void;

  // Event Handlers
  onOrderAdded: (callback: (data: OrderAction) => void) => void;
  onOrderCanceled: (callback: (data: OrderAction) => void) => void;
  onOrderError: (callback: (data: any) => void) => void;
  onOrderbookUpdate: (callback: (data: OrderBookData) => void) => void;
  onTradeUpdate: (callback: (data: TradeDataCex) => void) => void;
  onMarketStatsUpdate: (callback: (data: MarketStatsData) => void) => void;
  onKlinesData: (callback: (data: KlinesData) => void) => void;
  onEngineHeartbeat: (callback: (data: any) => void) => void;
  onConnected: (callback: (data: any) => void) => void;
  onAuthenticated: (callback: (data: any) => void) => void;
  onDisconnected: (callback: (reason: string) => void) => void;
  onError: (callback: (error: any) => void) => void;
  onConnectError: (callback: (error: any) => void) => void;
}

// ================= Socket.IO Implementation ================= //

let socketInstance: CexEngineSocketService | null = null;

const createCexEngineSocketInstance = (wsUrl: string, authToken?: string): CexEngineSocketService => {
  const socket = io(wsUrl, {
    transports: ['websocket', 'polling'],
    auth: {
      token: authToken
    },
    query: {
      token: authToken
    },
    timeout: 30000,
    forceNew: true,
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  });

  let isAuthenticated = false;
  let currentUserId: string | undefined;
  let currentUsername: string | undefined;

  // Connection management
  const connect = () => {
    if (!socket.connected) {
      socket.connect();
    }
  };

  const disconnect = () => {
    if (socket.connected) {
      socket.disconnect();
    }
  };

  const isConnected = () => socket.connected;

  // Authentication
  const authenticate = (token: string): Promise<WebSocketResponse> => {
    return new Promise((resolve) => {
      const requestId = uuidv4();

      const responseHandler = (data: any) => {
        if (data.event === 'authenticated') {
          isAuthenticated = true;
          currentUserId = data.data.userId;
          currentUsername = data.data.username;
          socket.off('authenticated', responseHandler);
          socket.off('error', errorHandler);
          resolve({
            success: true,
            data: data.data,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      const errorHandler = (data: any) => {
        socket.off('authenticated', responseHandler);
        socket.off('error', errorHandler);
        resolve({
          success: false,
          error: data.message || 'Authentication failed',
          timestamp: Date.now(),
          requestId
        });
      };

      socket.on('authenticated', responseHandler);
      socket.on('error', errorHandler);

      socket.emit('authenticate', { token }, (response: any) => {
        if (response && response.event === 'authenticated') {
          isAuthenticated = true;
          currentUserId = response.data.userId;
          currentUsername = response.data.username;
          resolve({
            success: true,
            data: response.data,
            timestamp: Date.now(),
            requestId
          });
        }
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        socket.off('authenticated', responseHandler);
        socket.off('error', errorHandler);
        resolve({
          success: false,
          error: 'Authentication timeout',
          timestamp: Date.now(),
          requestId
        });
      }, 10000);
    });
  };

  const getConnectionStatus = (): Promise<ConnectionStatusCex> => {
    return new Promise((resolve) => {
      const requestId = uuidv4();

      const responseHandler = (data: any) => {
        if (data.event === 'connectionStatus') {
          socket.off('connectionStatus', responseHandler);
          resolve(data.data);
        }
      };

      socket.on('connectionStatus', responseHandler);
      socket.emit('getConnectionStatus', { requestId });

      // Timeout after 5 seconds
      setTimeout(() => {
        socket.off('connectionStatus', responseHandler);
        resolve({ authenticated: isAuthenticated });
      }, 5000);
    });
  };

  // Order Management
  const addOrder = (orderData: SocketOrderRequest): Promise<WebSocketResponse> => {
    return new Promise((resolve) => {
      const requestId = uuidv4();

      const responseHandler = (data: any) => {
        if (data.event === 'orderRequestSent' && data.data.requestId === requestId) {
          socket.off('orderRequestSent', responseHandler);
          socket.off('orderError', errorHandler);
          resolve({
            success: true,
            data: data.data,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      const errorHandler = (data: any) => {
        if (data.data.requestId === requestId) {
          socket.off('orderRequestSent', responseHandler);
          socket.off('orderError', errorHandler);
          resolve({
            success: false,
            error: data.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      socket.on('orderRequestSent', responseHandler);
      socket.on('orderError', errorHandler);

      socket.emit('addOrder', { orderData, requestId }, (response: any) => {
        if (response && response.event === 'orderRequestSent') {
          resolve({
            success: true,
            data: response.data,
            timestamp: Date.now(),
            requestId
          });
        } else if (response && response.event === 'orderError') {
          resolve({
            success: false,
            error: response.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      });
      resolve({
        success: false,
        error: 'Order request timeout',
        timestamp: Date.now(),
        requestId
      });
      // Timeout after 30 seconds
      setTimeout(() => {
        socket.off('orderRequestSent', responseHandler);
        socket.off('orderError', errorHandler);
        resolve({
          success: false,
          error: 'Order request timeout',
          timestamp: Date.now(),
          requestId
        });
      }, 30000);
    });
  };

  const cancelOrder = (cancelData: SocketCancelOrderRequest): Promise<WebSocketResponse> => {
    return new Promise((resolve) => {
      const requestId = uuidv4();

      const responseHandler = (data: any) => {
        if (data.event === 'cancelRequestSent' && data.data.requestId === requestId) {
          socket.off('cancelRequestSent', responseHandler);
          socket.off('orderError', errorHandler);
          resolve({
            success: true,
            data: data.data,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      const errorHandler = (data: any) => {
        if (data.data.requestId === requestId) {
          socket.off('cancelRequestSent', responseHandler);
          socket.off('orderError', errorHandler);
          resolve({
            success: false,
            error: data.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      socket.on('cancelRequestSent', responseHandler);
      socket.on('orderError', errorHandler);

      socket.emit('cancelOrder', { cancelData, requestId }, (response: any) => {
        if (response && response.event === 'cancelRequestSent') {
          resolve({
            success: true,
            data: response.data,
            timestamp: Date.now(),
            requestId
          });
        } else if (response && response.event === 'orderError') {
          resolve({
            success: false,
            error: response.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      });
      resolve({
        success: true,
        data: null,
        timestamp: Date.now(),
        requestId
      });

      // Timeout after 30 seconds
      setTimeout(() => {
        socket.off('cancelRequestSent', responseHandler);
        socket.off('orderError', errorHandler);
        resolve({
          success: false,
          error: 'Cancel request timeout',
          timestamp: Date.now(),
          requestId
        });
      }, 30000);
    });
  };

  // Market Data Subscriptions
  const subscribeKlines = (request: SocketKlinesRequest): Promise<WebSocketResponse> => {
    return new Promise((resolve) => {
      const requestId = uuidv4();

      const responseHandler = (data: any) => {
        if (data.event === 'klinesSubscribed' && data.data.requestId === requestId) {
          socket.off('klinesSubscribed', responseHandler);
          socket.off('subscriptionError', errorHandler);
          resolve({
            success: true,
            data: data.data,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      const errorHandler = (data: any) => {
        if (data.data.requestId === requestId) {
          socket.off('klinesSubscribed', responseHandler);
          socket.off('subscriptionError', errorHandler);
          resolve({
            success: false,
            error: data.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      socket.on('klinesSubscribed', responseHandler);
      socket.on('subscriptionError', errorHandler);

      socket.emit('subscribeKlines', { ...request, requestId }, (response: any) => {
        if (response && response.event === 'klinesSubscribed') {
          resolve({
            success: true,
            data: response.data,
            timestamp: Date.now(),
            requestId
          });
        } else if (response && response.event === 'subscriptionError') {
          resolve({
            success: false,
            error: response.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        socket.off('klinesSubscribed', responseHandler);
        socket.off('subscriptionError', errorHandler);
        resolve({
          success: false,
          error: 'Subscription timeout',
          timestamp: Date.now(),
          requestId
        });
      }, 10000);
    });
  };

  const unsubscribeKlines = (symbol: string): Promise<WebSocketResponse> => {
    return new Promise((resolve) => {
      const requestId = uuidv4();

      const responseHandler = (data: any) => {
        if (data.event === 'klinesUnsubscribed' && data.data.requestId === requestId) {
          socket.off('klinesUnsubscribed', responseHandler);
          socket.off('subscriptionError', errorHandler);
          resolve({
            success: true,
            data: data.data,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      const errorHandler = (data: any) => {
        if (data.data.requestId === requestId) {
          socket.off('klinesUnsubscribed', responseHandler);
          socket.off('subscriptionError', errorHandler);
          resolve({
            success: false,
            error: data.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      socket.on('klinesUnsubscribed', responseHandler);
      socket.on('subscriptionError', errorHandler);

      socket.emit('unsubscribeKlines', { symbol, requestId }, (response: any) => {
        if (response && response.event === 'klinesUnsubscribed') {
          resolve({
            success: true,
            data: response.data,
            timestamp: Date.now(),
            requestId
          });
        } else if (response && response.event === 'subscriptionError') {
          resolve({
            success: false,
            error: response.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        socket.off('klinesUnsubscribed', responseHandler);
        socket.off('subscriptionError', errorHandler);
        resolve({
          success: false,
          error: 'Unsubscription timeout',
          timestamp: Date.now(),
          requestId
        });
      }, 10000);
    });
  };

  const subscribeOrderbook = (request: SocketOrderbookRequest): Promise<WebSocketResponse> => {
    return new Promise((resolve) => {
      const requestId = uuidv4();

      const responseHandler = (data: any) => {
        if (data.event === 'orderbookSubscribed' && data.data.requestId === requestId) {
          socket.off('orderbookSubscribed', responseHandler);
          socket.off('subscriptionError', errorHandler);
          resolve({
            success: true,
            data: data.data,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      const errorHandler = (data: any) => {
        if (data.data.requestId === requestId) {
          socket.off('orderbookSubscribed', responseHandler);
          socket.off('subscriptionError', errorHandler);
          resolve({
            success: false,
            error: data.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      socket.on('orderbookSubscribed', responseHandler);
      socket.on('subscriptionError', errorHandler);

      socket.emit('subscribeOrderbook', { ...request, requestId }, (response: any) => {
        if (response && response.event === 'orderbookSubscribed') {
          resolve({
            success: true,
            data: response.data,
            timestamp: Date.now(),
            requestId
          });
        } else if (response && response.event === 'subscriptionError') {
          resolve({
            success: false,
            error: response.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        socket.off('orderbookSubscribed', responseHandler);
        socket.off('subscriptionError', errorHandler);
        resolve({
          success: false,
          error: 'Subscription timeout',
          timestamp: Date.now(),
          requestId
        });
      }, 10000);
    });
  };

  const unsubscribeOrderbook = (symbol: string): Promise<WebSocketResponse> => {
    return new Promise((resolve) => {
      const requestId = uuidv4();

      const responseHandler = (data: any) => {
        if (data.event === 'orderbookUnsubscribed' && data.data.requestId === requestId) {
          socket.off('orderbookUnsubscribed', responseHandler);
          socket.off('subscriptionError', errorHandler);
          resolve({
            success: true,
            data: data.data,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      const errorHandler = (data: any) => {
        if (data.data.requestId === requestId) {
          socket.off('orderbookUnsubscribed', responseHandler);
          socket.off('subscriptionError', errorHandler);
          resolve({
            success: false,
            error: data.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      socket.on('orderbookUnsubscribed', responseHandler);
      socket.on('subscriptionError', errorHandler);

      socket.emit('unsubscribeOrderbook', { symbol, requestId }, (response: any) => {
        if (response && response.event === 'orderbookUnsubscribed') {
          resolve({
            success: true,
            data: response.data,
            timestamp: Date.now(),
            requestId
          });
        } else if (response && response.event === 'subscriptionError') {
          resolve({
            success: false,
            error: response.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        socket.off('orderbookUnsubscribed', responseHandler);
        socket.off('subscriptionError', errorHandler);
        resolve({
          success: false,
          error: 'Unsubscription timeout',
          timestamp: Date.now(),
          requestId
        });
      }, 10000);
    });
  };

  const subscribeMarketStats = (request: SocketMarketStatsRequest): Promise<WebSocketResponse> => {
    return new Promise((resolve) => {
      const requestId = uuidv4();

      const responseHandler = (data: any) => {
        if (data.event === 'marketStatsSubscribed' && data.data.requestId === requestId) {
          socket.off('marketStatsSubscribed', responseHandler);
          socket.off('subscriptionError', errorHandler);
          resolve({
            success: true,
            data: data.data,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      const errorHandler = (data: any) => {
        if (data.data.requestId === requestId) {
          socket.off('marketStatsSubscribed', responseHandler);
          socket.off('subscriptionError', errorHandler);
          resolve({
            success: false,
            error: data.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      socket.on('marketStatsSubscribed', responseHandler);
      socket.on('subscriptionError', errorHandler);

      socket.emit('subscribeMarketStats', { ...request, requestId }, (response: any) => {
        if (response && response.event === 'marketStatsSubscribed') {
          resolve({
            success: true,
            data: response.data,
            timestamp: Date.now(),
            requestId
          });
        } else if (response && response.event === 'subscriptionError') {
          resolve({
            success: false,
            error: response.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        socket.off('marketStatsSubscribed', responseHandler);
        socket.off('subscriptionError', errorHandler);
        resolve({
          success: false,
          error: 'Subscription timeout',
          timestamp: Date.now(),
          requestId
        });
      }, 10000);
    });
  };

  const unsubscribeMarketStats = (symbol: string): Promise<WebSocketResponse> => {
    return new Promise((resolve) => {
      const requestId = uuidv4();

      const responseHandler = (data: any) => {
        if (data.event === 'marketStatsUnsubscribed' && data.data.requestId === requestId) {
          socket.off('marketStatsUnsubscribed', responseHandler);
          socket.off('subscriptionError', errorHandler);
          resolve({
            success: true,
            data: data.data,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      const errorHandler = (data: any) => {
        if (data.data.requestId === requestId) {
          socket.off('marketStatsUnsubscribed', responseHandler);
          socket.off('subscriptionError', errorHandler);
          resolve({
            success: false,
            error: data.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      socket.on('marketStatsUnsubscribed', responseHandler);
      socket.on('subscriptionError', errorHandler);

      socket.emit('unsubscribeMarketStats', { symbol, requestId }, (response: any) => {
        if (response && response.event === 'marketStatsUnsubscribed') {
          resolve({
            success: true,
            data: response.data,
            timestamp: Date.now(),
            requestId
          });
        } else if (response && response.event === 'subscriptionError') {
          resolve({
            success: false,
            error: response.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        socket.off('marketStatsUnsubscribed', responseHandler);
        socket.off('subscriptionError', errorHandler);
        resolve({
          success: false,
          error: 'Unsubscription timeout',
          timestamp: Date.now(),
          requestId
        });
      }, 10000);
    });
  };

  const subscribeTrades = (request: SocketTradesRequest): Promise<WebSocketResponse> => {
    return new Promise((resolve) => {
      const requestId = uuidv4();

      const responseHandler = (data: any) => {
        if (data.event === 'tradesSubscribed' && data.data.requestId === requestId) {
          socket.off('tradesSubscribed', responseHandler);
          socket.off('subscriptionError', errorHandler);
          resolve({
            success: true,
            data: data.data,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      const errorHandler = (data: any) => {
        if (data.data.requestId === requestId) {
          socket.off('tradesSubscribed', responseHandler);
          socket.off('subscriptionError', errorHandler);
          resolve({
            success: false,
            error: data.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      socket.on('tradesSubscribed', responseHandler);
      socket.on('subscriptionError', errorHandler);

      socket.emit('subscribeTrades', { ...request, requestId }, (response: any) => {
        if (response && response.event === 'tradesSubscribed') {
          resolve({
            success: true,
            data: response.data,
            timestamp: Date.now(),
            requestId
          });
        } else if (response && response.event === 'subscriptionError') {
          resolve({
            success: false,
            error: response.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        socket.off('tradesSubscribed', responseHandler);
        socket.off('subscriptionError', errorHandler);
        resolve({
          success: false,
          error: 'Subscription timeout',
          timestamp: Date.now(),
          requestId
        });
      }, 10000);
    });
  };

  const unsubscribeTrades = (symbol: string): Promise<WebSocketResponse> => {
    return new Promise((resolve) => {
      const requestId = uuidv4();

      const responseHandler = (data: any) => {
        if (data.event === 'tradesUnsubscribed' && data.data.requestId === requestId) {
          socket.off('tradesUnsubscribed', responseHandler);
          socket.off('subscriptionError', errorHandler);
          resolve({
            success: true,
            data: data.data,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      const errorHandler = (data: any) => {
        if (data.data.requestId === requestId) {
          socket.off('tradesUnsubscribed', responseHandler);
          socket.off('subscriptionError', errorHandler);
          resolve({
            success: false,
            error: data.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      socket.on('tradesUnsubscribed', responseHandler);
      socket.on('subscriptionError', errorHandler);

      socket.emit('unsubscribeTrades', { symbol, requestId }, (response: any) => {
        if (response && response.event === 'tradesUnsubscribed') {
          resolve({
            success: true,
            data: response.data,
            timestamp: Date.now(),
            requestId
          });
        } else if (response && response.event === 'subscriptionError') {
          resolve({
            success: false,
            error: response.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        socket.off('tradesUnsubscribed', responseHandler);
        socket.off('subscriptionError', errorHandler);
        resolve({
          success: false,
          error: 'Unsubscription timeout',
          timestamp: Date.now(),
          requestId
        });
      }, 10000);
    });
  };

  // InfluxDB Data
  const getCandlesFromInflux = (request: SocketCandlesRequest): Promise<WebSocketResponse<CandlesFromInfluxData>> => {
    return new Promise((resolve) => {
      const requestId = uuidv4();

      const responseHandler = (data: any) => {
        if (data.event === 'candlesFromInflux' && data.data.requestId === requestId) {
          socket.off('candlesFromInflux', responseHandler);
          socket.off('candlesError', errorHandler);
          resolve({
            success: true,
            data: data.data,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      const errorHandler = (data: any) => {
        if (data.data.requestId === requestId) {
          socket.off('candlesFromInflux', responseHandler);
          socket.off('candlesError', errorHandler);
          resolve({
            success: false,
            error: data.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      socket.on('candlesFromInflux', responseHandler);
      socket.on('candlesError', errorHandler);

      socket.emit('getCandlesFromInflux', { ...request, requestId }, (response: any) => {
        if (response && response.event === 'candlesFromInflux') {
          resolve({
            success: true,
            data: response.data,
            timestamp: Date.now(),
            requestId
          });
        } else if (response && response.event === 'candlesError') {
          resolve({
            success: false,
            error: response.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      });

      // Timeout after 15 seconds
      setTimeout(() => {
        socket.off('candlesFromInflux', responseHandler);
        socket.off('candlesError', errorHandler);
        resolve({
          success: false,
          error: 'Request timeout',
          timestamp: Date.now(),
          requestId
        });
      }, 15000);
    });
  };

  const getAggregatedMarketStats = (request: SocketAggregatedMarketStatsRequest): Promise<WebSocketResponse<AggregatedMarketStatsData>> => {
    return new Promise((resolve) => {
      const requestId = uuidv4();

      const responseHandler = (data: any) => {
        if (data.event === 'aggregatedMarketStats' && data.data.requestId === requestId) {
          socket.off('aggregatedMarketStats', responseHandler);
          socket.off('aggregatedMarketStatsError', errorHandler);
          resolve({
            success: true,
            data: data.data,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      const errorHandler = (data: any) => {
        if (data.data.requestId === requestId) {
          socket.off('aggregatedMarketStats', responseHandler);
          socket.off('aggregatedMarketStatsError', errorHandler);
          resolve({
            success: false,
            error: data.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      socket.on('aggregatedMarketStats', responseHandler);
      socket.on('aggregatedMarketStatsError', errorHandler);

      socket.emit('getAggregatedMarketStats', { ...request, requestId }, (response: any) => {
        if (response && response.event === 'aggregatedMarketStats') {
          resolve({
            success: true,
            data: response.data,
            timestamp: Date.now(),
            requestId
          });
        } else if (response && response.event === 'aggregatedMarketStatsError') {
          resolve({
            success: false,
            error: response.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      });

      // Timeout after 15 seconds
      setTimeout(() => {
        socket.off('aggregatedMarketStats', responseHandler);
        socket.off('aggregatedMarketStatsError', errorHandler);
        resolve({
          success: false,
          error: 'Request timeout',
          timestamp: Date.now(),
          requestId
        });
      }, 15000);
    });
  };

  // Redis Cache Data
  const getOrderBookFromRedis = (request: SocketOrderbookRequest): Promise<WebSocketResponse<OrderBookResponse>> => {
    return new Promise((resolve) => {
      const requestId = uuidv4();

      const responseHandler = (data: any) => {
        if (data.event === 'orderBookFromRedis' && data.data.requestId === requestId) {
          socket.off('orderBookFromRedis', responseHandler);
          socket.off('orderBookError', errorHandler);
          resolve({
            success: true,
            data: data.data,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      const errorHandler = (data: any) => {
        if (data.data.requestId === requestId) {
          socket.off('orderBookFromRedis', responseHandler);
          socket.off('orderBookError', errorHandler);
          resolve({
            success: false,
            error: data.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      socket.on('orderBookFromRedis', responseHandler);
      socket.on('orderBookError', errorHandler);

      socket.emit('getOrderBookFromRedis', { ...request, requestId }, (response: any) => {
        if (response && response.event === 'orderBookFromRedis') {
          resolve({
            success: true,
            data: response.data,
            timestamp: Date.now(),
            requestId
          });
        } else if (response && response.event === 'orderBookError') {
          resolve({
            success: false,
            error: response.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        socket.off('orderBookFromRedis', responseHandler);
        socket.off('orderBookError', errorHandler);
        resolve({
          success: false,
          error: 'Request timeout',
          timestamp: Date.now(),
          requestId
        });
      }, 10000);
    });
  };

  const getMarketStatsFromRedis = (request: SocketMarketStatsRequest): Promise<WebSocketResponse<MarketStatsData>> => {
    return new Promise((resolve) => {
      const requestId = uuidv4();

      const responseHandler = (data: any) => {
        if (data.event === 'marketStatsFromRedis' && data.data.requestId === requestId) {
          socket.off('marketStatsFromRedis', responseHandler);
          socket.off('marketStatsError', errorHandler);
          resolve({
            success: true,
            data: data.data,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      const errorHandler = (data: any) => {
        if (data.data.requestId === requestId) {
          socket.off('marketStatsFromRedis', responseHandler);
          socket.off('marketStatsError', errorHandler);
          resolve({
            success: false,
            error: data.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      };

      socket.on('marketStatsFromRedis', responseHandler);
      socket.on('marketStatsError', errorHandler);

      socket.emit('getMarketStatsFromRedis', { ...request, requestId }, (response: any) => {
        if (response && response.event === 'marketStatsFromRedis') {
          resolve({
            success: true,
            data: response.data,
            timestamp: Date.now(),
            requestId
          });
        } else if (response && response.event === 'marketStatsError') {
          resolve({
            success: false,
            error: response.data.error,
            timestamp: Date.now(),
            requestId
          });
        }
      });

      // Timeout after 10 seconds
      setTimeout(() => {
        socket.off('marketStatsFromRedis', responseHandler);
        socket.off('marketStatsError', errorHandler);
        resolve({
          success: false,
          error: 'Request timeout',
          timestamp: Date.now(),
          requestId
        });
      }, 10000);
    });
  };

  // Utility
  const ping = (): void => {
    if (socket.connected) {
      socket.emit('ping', {}, (response: any) => {
        console.log('Ping response:', response);
      });
    }
  };

  const subscribe = (channels: string[]): void => {
    socket.emit('subscribe', { channels }, (response: any) => {
      console.log('Subscribe response:', response);
    });
  };

  const unsubscribe = (channels: string[]): void => {
    socket.emit('unsubscribe', { channels }, (response: any) => {
      console.log('Unsubscribe response:', response);
    });
  };

  // Event Handlers
  const onOrderAdded = (callback: (data: any) => void): void => {
    socket.on('orderAdded', callback);
  };

  const onOrderCanceled = (callback: (data: any) => void): void => {
    socket.on('orderCanceled', callback);
  };

  const onOrderError = (callback: (data: any) => void): void => {
    socket.on('orderError', callback);
  };

  const onOrderbookUpdate = (callback: (data: OrderBookData) => void): void => {
    const internalCallback = (data: OrderBookResponse) => {
      callback(orderBookKucoinToCex(data, data.symbol));
    };
    socket.on('orderbook_update', internalCallback);
  };

  const onTradeUpdate = (callback: (data: TradeDataCex) => void): void => {
    socket.on('trade_update', callback);
  };

  const onMarketStatsUpdate = (callback: (data: MarketStatsData) => void): void => {
    socket.on('market_stats_update', callback);
  };

  const onKlinesData = (callback: (data: KlinesData) => void): void => {
    socket.on('klines_data', callback);
  };

  const onEngineHeartbeat = (callback: (data: any) => void): void => {
    socket.on('engine_heartbeat', callback);
  };

  const onConnected = (callback: (data: any) => void): void => {
    socket.on('connected', callback);
  };

  const onAuthenticated = (callback: (data: any) => void): void => {
    socket.on('authenticated', callback);
  };

  const onDisconnected = (callback: (reason: string) => void): void => {
    socket.on('disconnect', callback);
  };

  const onError = (callback: (error: any) => void): void => {
    socket.on('error', callback);
  };

  const onConnectError = (callback: (error: any) => void): void => {
    socket.on('connect_error', callback);
  };

  // Return the service interface
  return {
    socket,
    connect,
    disconnect,
    isConnected,
    authenticate,
    getConnectionStatus,
    addOrder,
    cancelOrder,
    subscribeKlines,
    unsubscribeKlines,
    subscribeOrderbook,
    unsubscribeOrderbook,
    subscribeMarketStats,
    unsubscribeMarketStats,
    subscribeTrades,
    unsubscribeTrades,
    getCandlesFromInflux,
    getAggregatedMarketStats,
    getOrderBookFromRedis,
    getMarketStatsFromRedis,
    ping,
    subscribe,
    unsubscribe,
    onOrderAdded,
    onOrderCanceled,
    onOrderError,
    onOrderbookUpdate,
    onTradeUpdate,
    onMarketStatsUpdate,
    onKlinesData,
    onEngineHeartbeat,
    onConnected,
    onAuthenticated,
    onDisconnected,
    onError,
    onConnectError,
  };
};

// ================= Factory Function ================= //

export const connectCexEngineSocket = (wsUrl: string, authToken?: string): CexEngineSocketService => {
  if (!socketInstance) {
    socketInstance = createCexEngineSocketInstance(wsUrl, authToken);
  }
  return socketInstance;
};

export const getCexEngineSocket = (): CexEngineSocketService | null => {
  return socketInstance;
};

export const disconnectCexEngineSocket = (): void => {
  if (socketInstance) {
    socketInstance.disconnect();
    socketInstance = null;
  }
};
